import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Moon, Sun, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme, useRouter } from '../App';
import logoImage from '../assets/coremonit-logo.svg';

export function Header() {
  const { theme, toggleTheme } = useTheme();
  const { currentPage, navigate } = useRouter();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', page: 'home' },
    { name: 'Features', page: 'features' },
    { name: 'Pricing', page: 'pricing' },
    { name: 'Docs', page: 'docs' },
    { name: 'Blog', page: 'blog' },
    { name: 'Contact', page: 'contact' },
  ];

  return (
    <motion.header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-background/90 backdrop-blur-xl border-b border-cyber-cyan/20 shadow-lg shadow-cyber-cyan/5'
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <motion.div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate('home')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyber-cyan via-cyber-blue to-cyber-purple p-0.5">
                <div className="w-full h-full rounded-xl bg-background flex items-center justify-center">
                  <img 
                    src={logoImage} 
                    alt="CoreMonit Logo" 
                    className="w-6 h-6 object-contain"
                  />
                </div>
              </div>
              <motion.div 
                className="absolute inset-0 bg-gradient-to-br from-cyber-cyan/30 via-cyber-blue/30 to-cyber-purple/30 rounded-xl blur-md"
                animate={{ opacity: [0.4, 0.8, 0.4] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple bg-clip-text text-transparent">
              CoreMonit
            </span>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={() => navigate(item.page)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 relative ${
                  currentPage === item.page
                    ? 'text-cyber-cyan'
                    : 'text-foreground/70 hover:text-foreground hover:bg-cyber-cyan/5'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.name}
                {currentPage === item.page && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyber-cyan to-cyber-purple"
                    layoutId="activeTab"
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.button>
            ))}
          </nav>

          {/* Right side - Theme toggle & CTA */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <motion.button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-muted/50 hover:bg-cyber-cyan/10 border border-transparent hover:border-cyber-cyan/20 transition-all duration-300"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4 text-yellow-400" />
              ) : (
                <Moon className="w-4 h-4 text-cyber-blue" />
              )}
            </motion.button>

            {/* Sign In Button */}
            <Button
              variant="ghost"
              className="hidden sm:inline-flex"
              onClick={() => navigate('signin')}
            >
              Sign In
            </Button>

            {/* CTA Button */}
            <motion.div 
              whileHover={{ scale: 1.05, y: -1 }} 
              whileTap={{ scale: 0.95 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity duration-300" />
              <Button
                onClick={() => navigate('signup')}
                className="relative bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple hover:from-cyber-cyan/90 hover:via-cyber-blue/90 hover:to-cyber-purple/90 text-white border-0 shadow-lg shadow-cyber-blue/25"
              >
                <span className="hidden sm:inline">Start Free Trial</span>
                <span className="sm:hidden">Trial</span>
              </Button>
            </motion.div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors duration-300"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <motion.div
          className={`lg:hidden overflow-hidden ${isMobileMenuOpen ? 'max-h-96' : 'max-h-0'}`}
          initial={false}
          animate={{ maxHeight: isMobileMenuOpen ? 384 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="py-4 space-y-2 border-t border-border/50">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  navigate(item.page);
                  setIsMobileMenuOpen(false);
                }}
                className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  currentPage === item.page
                    ? 'text-cyan-500 bg-cyan-500/10'
                    : 'text-foreground/70 hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {item.name}
              </button>
            ))}
            <Button
              variant="ghost"
              className="w-full justify-start px-4 py-2"
              onClick={() => {
                navigate('signin');
                setIsMobileMenuOpen(false);
              }}
            >
              Sign In
            </Button>
          </div>
        </motion.div>
      </div>
    </motion.header>
  );
}